import { useState, useEffect } from "react";
import TaskResolutionDialog from "./TaskResolutionDialog";
import { useSession } from "../../context/AuthContext";
import { usePlans } from "../../context/PlanContext";
import api from "../../services/api";

interface SubmissionItem {
  id: string;
  taskId: string;
  userId: string;
  studentName: string;
  studentEmail: string;
  membership: string;
  taskTitle: string;
  courseTitle: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  updatedAt: string;
  contentUrl: string;
  evidenceUrl: string;
}

export default function TaskApprovals() {
  const { data: session } = useSession();
  const { getPlanByName } = usePlans();
  const [items, setItems] = useState<SubmissionItem[]>([]);
  const [activeTab, setActiveTab] = useState<"PENDING" | "APPROVED" | "REJECTED">("PENDING");
  const [resolutionItem, setResolutionItem] = useState<SubmissionItem | null>(null);

  const fetchSubmissions = async () => {
    try {
      const userId = session?.user?.id;
      const url = userId 
        ? `/staff/all-submissions?userId=${userId}`
        : `/staff/all-submissions`;
      
      const res = await api.get(url);
      setItems(res.data);
    } catch (err) {
      console.error("Failed to fetch submissions:", err);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    const interval = setInterval(fetchSubmissions, 10000);
    return () => clearInterval(interval);
  }, [session]);

  const [searchEmail, setSearchEmail] = useState("");

  const filteredItems = items.filter(item => {
    if (item.status !== activeTab) return false;
    if (searchEmail.trim() !== "") {
      return item.studentEmail?.toLowerCase().includes(searchEmail.toLowerCase());
    }
    return true;
  });

  return (
    <div className="space-y-6 sm:space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      <TaskResolutionDialog 
        isOpen={!!resolutionItem} 
        onClose={() => setResolutionItem(null)}
        item={resolutionItem}
        onSuccess={fetchSubmissions}
      />

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-outline-variant/10">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-on-surface tracking-tighter">Task Approvals</h2>
          <p className="text-on-surface-variant font-medium text-xs sm:text-sm">Review student tasks and provide helpful feedback.</p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative w-full sm:w-auto">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/50 text-sm">search</span>
            <input 
              type="text" 
              placeholder="Search by email..." 
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="pl-10 pr-4 py-2.5 sm:py-3 rounded-[1.5rem] bg-surface-container-high border border-outline-variant/10 text-xs font-bold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary transition-colors w-full sm:w-64"
            />
          </div>

          <div className="flex items-center bg-surface-container-high p-1 sm:p-1.5 rounded-[1.5rem] sm:rounded-[2rem] border border-outline-variant/10 shadow-lg w-fit overflow-x-auto">
          <button 
            onClick={() => setActiveTab("PENDING")}
            className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-full flex items-center gap-2 transition-all ${
              activeTab === "PENDING" ? "bg-on-surface text-surface shadow-xl font-bold" : "text-on-surface-variant hover:text-on-surface"
            }`}
            title="Pending Review"
          >
            <span className="material-symbols-outlined text-sm">pending_actions</span>
            <span className="text-[10px] font-black uppercase tracking-widest">{items.filter(i => i.status === "PENDING").length}</span>
          </button>
          
          <div className="w-px h-6 bg-outline-variant/20 mx-1 sm:mx-2 flex-shrink-0" />

          <button 
            onClick={() => setActiveTab("APPROVED")}
            className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-full flex items-center gap-2 transition-all ${
              activeTab === "APPROVED" ? "bg-emerald-505 bg-emerald-500 text-white shadow-xl shadow-emerald-500/20 font-bold" : "text-on-surface-variant hover:text-emerald-500"
            }`}
            title="Approved Mastery"
          >
            <span className="material-symbols-outlined text-sm">verified</span>
            <span className="text-[10px] font-black uppercase tracking-widest">{items.filter(i => i.status === "APPROVED").length}</span>
          </button>

          <button 
            onClick={() => setActiveTab("REJECTED")}
            className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-full flex items-center gap-2 transition-all ${
              activeTab === "REJECTED" ? "bg-red-500 text-white shadow-xl shadow-red-500/20 font-bold" : "text-on-surface-variant hover:text-red-500"
            }`}
            title="Revision Required"
          >
            <span className="material-symbols-outlined text-sm">assignment_return</span>
            <span className="text-[10px] font-black uppercase tracking-widest">{items.filter(i => i.status === "REJECTED").length}</span>
          </button>
        </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">
        {filteredItems.map((item) => (
          <div 
            key={item.id}
            onClick={() => setResolutionItem(item)}
            className={`bg-surface-container-low border rounded-[1.75rem] sm:rounded-[2.5rem] p-6 sm:p-7 transition-all hover:shadow-2xl hover:translate-y-[-4px] flex flex-col justify-between gap-6 h-full group cursor-pointer border-t-8 ${
              item.status === "PENDING" ? (
                (item.contentUrl || item.evidenceUrl) 
                  ? "border-violet-600 bg-violet-600/5" 
                  : "border-amber-500 bg-amber-500/5"
              ) : 
              item.status === "APPROVED" ? "border-emerald-500 opacity-90 shadow-lg shadow-emerald-500/10" : 
              item.status === "REJECTED" ? "border-red-500 opacity-90 shadow-lg shadow-red-500/10" :
              "border-outline-variant/20"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em]">#{item.id.slice(-6).toUpperCase()}</span>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-xs text-on-surface-variant/40">schedule</span>
                <span className="text-[10px] font-bold text-on-surface-variant/60">
                   {new Date(item.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>

            <div className="space-y-3 flex-1">
              <div className="space-y-1">
                <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-[0.15em] opacity-40">Curriculum Implementation</p>
                <h4 className="text-lg sm:text-xl font-black text-on-surface tracking-tighter leading-tight group-hover:text-primary transition-colors">
                  {item.taskTitle}
                </h4>
                <p className="text-[9px] font-black text-on-surface-variant/60 uppercase tracking-widest">{item.courseTitle}</p>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <div className="px-3 py-1 bg-surface-container-highest/50 text-on-surface-variant/60 rounded-full text-[9px] font-black uppercase tracking-widest border border-outline-variant/10 flex items-center gap-1.5 shadow-sm max-w-full overflow-hidden">
                  <span className="material-symbols-outlined text-[12px] shrink-0 opacity-40">person</span>
                  <span className="truncate">{item.studentName}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-outline-variant/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full animate-pulse ${
                    item.status === "PENDING" ? ((item.contentUrl || item.evidenceUrl) ? "bg-violet-600 shadow-[0_0_8px_rgba(139,92,246,0.5)]" : "bg-amber-500") : 
                    item.status === "APPROVED" ? "bg-emerald-500" : "bg-red-500"
                  }`} />
                  <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/70">
                    {item.status === "PENDING" ? ((item.contentUrl || item.evidenceUrl) ? "SUBMITTED" : "PENDING") : item.status}
                  </span>
                </div>
                <span 
                  className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter"
                  style={{
                    backgroundColor: getPlanByName(item.membership)?.icon_color ? `${getPlanByName(item.membership)?.icon_color}1A` : (
                      item.membership === "VIP" ? "rgba(6,182,212,0.1)" : 
                      item.membership === "PREMIUM" ? "rgba(245,158,11,0.1)" : 
                      "rgba(100,116,139,0.1)"
                    ),
                    color: getPlanByName(item.membership)?.icon_color || (
                      item.membership === "VIP" ? "#22d3ee" : 
                      item.membership === "PREMIUM" ? "#fbbf24" : 
                      "#94a3b8"
                    )
                  }}
                >
                  {item.membership}
                </span>
              </div>

              {item.status === "PENDING" ? (
                (item.contentUrl || item.evidenceUrl) ? (
                  <button 
                    className="w-full py-3.5 bg-violet-600 text-white rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl transition-all shadow-lg shadow-violet-600/20 active:scale-[0.98]"
                  >
                    Verify Mastery
                  </button>
                ) : (
                  <div className="w-full py-3.5 bg-amber-500/5 text-amber-600/50 border border-amber-500/10 rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-sm">hourglass_empty</span>
                    Waiting for Student
                  </div>
                )
              ) : (
                <div className={`w-full py-3.5 rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 ${
                  item.status === "APPROVED" ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"
                }`}>
                  <span className="material-symbols-outlined text-sm">
                    {item.status === "APPROVED" ? "verified" : "error"}
                  </span>
                  {item.status}
                </div>
              )}
            </div>
          </div>
        ))}

        {filteredItems.length === 0 && (
          <div className="col-span-full py-20 text-center space-y-4 bg-surface-container-lowest/50 rounded-[2rem] sm:rounded-[3rem] border-2 border-dashed border-outline-variant/10 px-6">
            <span className="material-symbols-outlined text-6xl text-on-surface-variant/20 animate-bounce">done_all</span>
            <p className="text-on-surface-variant font-medium italic opacity-60 text-sm">All student tasks have been reviewed!</p>
          </div>
        )}
      </div>
    </div>
  );
}
